import { NgClass } from '@angular/common';
import { Component, computed, forwardRef, input, signal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'ui-numeric-input-stepper',
  templateUrl: './numeric-input-stepper.component.html',
  styleUrl: './numeric-input-stepper.component.scss',
  standalone: true,
  imports: [NgClass, ButtonComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericInputStepperComponent),
      multi: true,
    },
  ],
})
export class NumericInputStepperComponent implements ControlValueAccessor {
  // Configuration inputs
  readonly min = input<number | undefined>(undefined);
  readonly max = input<number | undefined>(undefined);
  readonly step = input<number>(1);
  readonly allowDecimals = input<boolean>(false);
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  readonly showButtons = input<boolean>(true);

  // Internal signals
  private readonly _value = signal<number | null>(null);
  private readonly _touched = signal<boolean>(false);

  // Public readonly signals
  readonly value = this._value.asReadonly();
  readonly touched = this._touched.asReadonly();
  readonly canDecrement = computed(() => {
    const currentValue = this.value();
    const minValue = this.min();
    return (
      !this.disabled() &&
      !this.readonly() &&
      (currentValue === null || minValue === undefined || currentValue > minValue)
    );
  });
  readonly canIncrement = computed(() => {
    const currentValue = this.value();
    const maxValue = this.max();
    return (
      !this.disabled() &&
      !this.readonly() &&
      (currentValue === null || maxValue === undefined || currentValue < maxValue)
    );
  });

  readonly inputClasses = computed(() => {
    const baseClasses = [
      'w-full px-3 py-2 border rounded-md transition-colors duration-200',
      'focus:outline-none focus:border-success-500',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      'placeholder:text-gray-400',
      'border-secondary bg-white',
    ];

    // Size classes
    const sizeClasses = {
      sm: 'text-sm py-1.5 px-2',
      md: 'text-base py-2 px-3',
      lg: 'text-lg py-3 px-4',
    };

    return [...baseClasses, sizeClasses[this.size()]];
  });

  readonly buttonSize = computed(() => {
    const sizeMap = {
      sm: 'sm' as const,
      md: 'md' as const,
      lg: 'lg' as const,
    };
    return sizeMap[this.size()];
  });

  // ControlValueAccessor properties
  private onChange = (value: number | null) => {};
  private onTouched = () => {};

  // Static validator methods for use with reactive forms
  static min(minValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null; // Don't validate empty values
      }
      const numValue = Number(value);
      return !isNaN(numValue) && numValue < minValue ? { min: { actual: numValue, min: minValue } } : null;
    };
  }

  static max(maxValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null; // Don't validate empty values
      }
      const numValue = Number(value);
      return !isNaN(numValue) && numValue > maxValue ? { max: { actual: numValue, max: maxValue } } : null;
    };
  }

  static integer(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null; // Don't validate empty values
      }
      const numValue = Number(value);
      return !isNaN(numValue) && numValue % 1 !== 0 ? { integer: { actual: numValue } } : null;
    };
  }

  static step(stepValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '' || stepValue === 1) {
        return null; // Don't validate empty values or step of 1
      }
      const numValue = Number(value);
      return !isNaN(numValue) && numValue % stepValue !== 0 ? { step: { actual: numValue, step: stepValue } } : null;
    };
  }

  // ControlValueAccessor implementation
  writeValue(value: number | null): void {
    this._value.set(value);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Disabled state is handled through the disabled input
  }

  // Public methods
  increment(): void {
    if (!this.canIncrement()) return;

    const currentValue = this.value() ?? 0;
    const newValue = this.roundToStep(currentValue + this.step());
    this.updateValue(newValue);
  }

  decrement(): void {
    if (!this.canDecrement()) return;

    const currentValue = this.value() ?? 0;
    const newValue = this.roundToStep(currentValue - this.step());
    this.updateValue(newValue);
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;

    if (rawValue === '') {
      this.updateValue(null);
      return;
    }

    const numericValue = this.allowDecimals() ? parseFloat(rawValue) : parseInt(rawValue, 10);

    if (!isNaN(numericValue)) {
      this.updateValue(numericValue);
    }
  }

  onInputBlur(): void {
    this._touched.set(true);
    this.onTouched();
  }

  onInputFocus(): void {
    // Optional: Add focus handling if needed
  }

  // Private methods
  private updateValue(value: number | null): void {
    this._value.set(value);
    this.onChange(value);
  }

  private roundToStep(value: number): number {
    const step = this.step();
    if (step === 1 || this.allowDecimals()) {
      return Math.round(value / step) * step;
    }
    return Math.round(value);
  }
}
